/**
 * @module app.ext.iccc
 */

import { autoinject } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { DateValue } from 'app/shared/lib/date-time';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { CaseExternalSystemChangeEvent } from 'app/cm/lib/events';
import { IApiSettings } from 'app/shared/lib/api/api-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { I18N } from 'aurelia-i18n';
import * as api from 'app/shared/lib/essette-api';
import * as CM from 'app/cm/models/essette-api';

interface IPopupParameters {
    assessmentId?: string,
    memberFirstName?: string,
    memberMiddleName?: string | undefined,
    memberLastName?: string,
    memberDOB?: string,
    memberGender?: string,
    externalId?: string,
    additionalIds?: additionalId[],
    isCompleted?: boolean
}

interface additionalId {
    type: string,
    id: string
}

interface IIqcData {
    UUID: string,
    content_type: string,
    review_xml: string
}

type IqcGender = 'MALE' | 'FEMALE' | 'OTHER';

/**
 * Map a gender code to one of the valid IQC genders (`MALE`, `FEMALE`, or `OTHER`)
 * @param code a value of `M` or `F` for Male or Female, otherwise the result will be Other
 * @returns a gender code for IQC
 */
function mapIqcGenderCode(code?: string | null): IqcGender {
    if(code === 'F' || code === 'f') { return 'FEMALE'; }
    if(code === 'M' || code === 'm') { return 'MALE'; }
    return 'OTHER';
}

/**
 * Format a date (as `MM-dd-yyyy`) for passing to the IQC API
 * @param dateValue
 * @returns a date in the proper format
 */
function formatIqcDate(dateValue: DateValue): string {
    if(!dateValue.isValid) { return ''; }
    const { month, date, year } = dateValue.toParts();
    return `${String(month).padStart(2, '0')}-${String(date).padStart(2, '0')}-${String(year).padStart(4, '0')}`;
}

@autoinject
export class IcccManager {

    ////****************** Fields and Properties ************************************////
    private interQualConnectUrl: string = 'https://integ2.ds.interqual.com/service/session/saml2';
    private javascriptSourceUrl: string | null = null;

    private showPHI: boolean = false;
    private case: CM.Case | null = null;
    private assessmentAttachment: CM.CaseAssessmentAttachment | undefined = undefined;
    private settings: IApiSettings = {};

    private listener: (event: MessageEvent) => {};


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly api: api.Api,
        private readonly events: EventAggregator,
        private readonly logger: AppLogger,
        private readonly env: EnvironmentService,
        private readonly i18n: I18N) {

        this.logger.source = this;
        this.loadSettings();

        // Need to assign listener function to a class variable, so that it is only created and assigned once, and therefore can be added and removed as a window listener function
        this.listener = this.handleEventMessage.bind(this);
        window.addEventListener('message', this.listener, false);
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    protected async loadSettings(): Promise<void> {
        this.settings = await this.loadSystemSettings();
        const consumerServiceUrl = <string>getApiSetting(this.settings, 'iccc.saml.consumerServiceUrl.$url');
        if(consumerServiceUrl) this.interQualConnectUrl = consumerServiceUrl;
        const javascriptSourceUrl = <string>getApiSetting(this.settings, 'iccc.javascript.sourceUrl.$url');
        if(javascriptSourceUrl) this.javascriptSourceUrl = javascriptSourceUrl;
        else
            this.logger.error('Unable to set javascriptSourceUrl');
        if(<boolean>getApiSetting(this.settings, 'iccc.submitMemberInformation.$isEnabled') === true) {
            this.showPHI = true;
        }
    }

    public open(cmCase?: CM.Case, assessmentAttachment?: CM.CaseAssessmentAttachment, uuid?: string): void {
        this.case = cmCase || null;
        this.assessmentAttachment = assessmentAttachment;

        const params: IPopupParameters = {};
        if(uuid) { params.assessmentId = uuid; }
        if(this.assessmentAttachment) { params.isCompleted = this.assessmentAttachment.isCompleted ?? false }

        // //add member data to query string
        if(this.showPHI && cmCase?.member && cmCase.member.name && cmCase.member.birthDate) {
            params.memberFirstName = cmCase.member.name.first || undefined;
            params.memberMiddleName = cmCase.member.name.middle || undefined;
            params.memberLastName = cmCase.member.name.last || undefined;
            params.memberDOB = formatIqcDate(new DateValue(cmCase.member.birthDate)); //Format: MM-dd-yyyy
            params.memberGender = mapIqcGenderCode(cmCase.member.gender?.designation?.identity); //Allowed values: MALE, FEMALE, or OTHER
            params.externalId = cmCase.member.identifier?.external || '';

            params.additionalIds = [];
            // params.additionalIDs - Optional array for additional patient identifier types
            // • type – Required. Acceptable values: EncounterID, PayerMemberID, PayerID
            // • id – Required (1-255 ID characters)

        }

        // timestamp to avoid cache
        const query: Record<string, string> = {
            ts: String(new Date().getTime()),
            env: this.env.config.instanceType
        };

        const queryString = new URLSearchParams(query).toString();
        const w = window.open('app/ext/iccc/iccc-popup.html?' + queryString, 'iqcPopup', 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
        if(!w) {
            this.logger.warn('Unable to open external iqc pop-up');
            return;
        }
        // set the window values here
        (<any>w)['Essette.IQC.parameters'] = params;
        (<any>w)['Essette.IQC.sourceUrl'] = this.javascriptSourceUrl;

        w.onload = async () => {
            const response = await this.getSAMLResponse();
            if(!response?.resultText) { throw new Error('[IQC] Error retrieving SAMl token'); }
            w.document.title = this.i18n.tr('ui:titles.essette') + ' / ' + this.i18n.tr('ext:iqc.integration.$');
            w.document.getElementById('closeButton')!.textContent = this.i18n.tr('ui:actions.returnTo', { what: this.i18n.tr('ui:titles.essette') });

            const frame: HTMLIFrameElement = <HTMLIFrameElement>w.document.getElementById('iqconnect');
            if(frame.contentWindow) {
                frame.contentWindow.document.write('<!DOCTYPE html><html><body>Opening InterQual Coordinated Care Please Wait...</body></html>');
                const f: HTMLFormElement = frame.contentWindow.document.createElement('form');
                const i: HTMLInputElement = frame.contentWindow.document.createElement('input');
                f.method = 'post';
                i.name = 'SAMLResponse';
                i.hidden = true;
                i.value = btoa(response.resultText || '');
                f.appendChild(i);
                f.action = this.interQualConnectUrl;
                frame.contentWindow.document.getElementsByTagName('body')[0].appendChild(f);
                f.submit();
            }
        };

    }

    private async handleEventMessage(event: MessageEvent): Promise<void> {
        // NOTE [Eric] IQC event content is inconsistent, providing the necessary assessment info either within the event or event.data
        let data;
        if(typeof event.data === 'object' || typeof event.data === 'string') {
            data = event.data;
        } else if(typeof event === 'object' || typeof event === 'string') {
            data = event;
        } else {
            return;
        }

        if(this.case && typeof data === 'object') {
            if('UUID' in data) {
                //Save assessmentAttachment
                console.log('[InterQualOnline] Saving assessmentAttachment');

                const response = await this.saveAssessmentAttachment(this.buildAssessmentAttachment(data));
                if(!this.assessmentAttachment && response.id) {
                    this.assessmentAttachment = {
                        id: response.id
                    };
                }

                this.events.publish(new CaseExternalSystemChangeEvent);
            }
        }
    }

    protected buildAssessmentAttachment(data: IIqcData): CM.CaseAssessmentAttachment {

        // parse xml
        const parser = new DOMParser();
        const review: Document = parser.parseFromString(data.review_xml, "text/xml");
        const $assessment = review.getElementsByTagName('CarePlan')[0].getElementsByTagName('Assessment')[0];
        const isCompleted = review.getElementsByTagName('CarePlan')[0].getAttribute("CurrentStatusDesc") === 'Completed';

        const attachment: CM.CaseAssessmentAttachment = {
            "id": this.assessmentAttachment ? this.assessmentAttachment.id : null,
            "isCompleted": isCompleted,
            "assessment": {
                "description": $assessment.getAttribute("SubsetDesc"),
                "externalSystem": {
                    "code": "ICCC"
                },
                "externalAssessmentIdentifier": $assessment.getAttribute("SubsetCID"),
                "version": {
                    "externalVersionIdentifier": $assessment.getAttribute("VersionCID"),
                    "versionName": $assessment.getAttribute("EditionDate")
                }
            },
            "case": {
                "id": this.case ? this.case.id : null
            },
            "externalContent": {
                "externalContentType": {
                    "code": "AssessmentJson"
                },
                "content": data.review_xml,
                "mediaType": {
                    "code": "data.xml"
                }
            }
        };

        return attachment;
    }

    ////****************** Service Calls ********************************************////
    @api.apiCall
    private async getSAMLResponse(): Promise<api.ApiPostResponse> {
        const action = new api.ApiAction('singlesignon', 'account', { code: 'ICCC' });
        return await this.api.post(action);
    }

    @api.apiCall
    public async saveAssessmentAttachment(assessmentAttachment: CM.CaseAssessmentAttachment): Promise<api.ApiPostResponse> {
        const action = new api.ApiAction('iccc.assessmentattachmentsave', `cm/cases/assessments/attachments`, assessmentAttachment);
        return await this.api.post(action);
    }

    @api.apiCall
    public async loadSystemSettings(): Promise<IApiSettings> {
        try {
            const response = await this.api.get(this.api.settings(`ICCC/Systems/`));
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
        }
    }
}
